import { FC, ReactElement, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { Avatar, LoadingSpinner } from '@/components'
import { IconButton, ModalContent, PacoModal, TextInput } from '@/compositions'
import { startChatConversationApiCall } from '@/entities/ChatConversation/ChatConversationService'
import { ChatUser } from '@/entities/ChatUser/ChatUser'
import { isFetchResultSuccessful } from '@/entities/FetchResult'
import { toStrictISOString } from '@/helpers/string'
import { trans } from '@/helpers/trans'
import { useTypedDispatch } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

import './NewConversationModal.scss'

const normalize = (value: string): string => toStrictISOString(value).toLowerCase()

interface NewConversationModalProps {
    chatUsers: ChatUser[]
    isLoading: boolean
    onClose: () => void
}

export const NewConversationModal: FC<NewConversationModalProps> = ({
    chatUsers,
    isLoading,
    onClose,
}): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const [query, setQuery] = useState('')
    const [isStarting, setIsStarting] = useState(false)

    const filteredUsers = useMemo(() => {
        const normalizedQuery = normalize(query)

        return chatUsers
            .filter(user => !! user.streamUserId)
            .filter(user => normalize(user.fullName).includes(normalizedQuery))
    }, [chatUsers, query])

    const handleSelectUser = async (userUuid: string): Promise<void> => {
        if (isStarting) {
            return
        }

        setIsStarting(true)

        const result = await startChatConversationApiCall(userUuid)

        if (! isFetchResultSuccessful(result)) {
            dispatch(setToast({ text: trans('pages.messages.startChatFailed'), type: ToastType.fail }))
            setIsStarting(false)

            return
        }

        const path = [
            trans('nav.communication.link'),
            trans('nav.communication.messages.link'),
            result.data,
        ].join('/')

        onClose()
        navigate(`/${path}`)
    }

    return (
        <PacoModal cardClassName="new-conversation-modal">
            <IconButton
                hideLabel
                icon="cross-close"
                text={trans('common.cancel')}
                onClick={onClose}
                className="new-conversation-modal__close"
            />

            <ModalContent title={trans('pages.messages.newChat')}>
                <TextInput
                    type="search"
                    value={query}
                    placeholder={trans('pages.messages.searchColleagues')}
                    onChange={setQuery}
                    className="new-conversation-modal__search"
                />

                {isLoading && <LoadingSpinner />}

                {! isLoading && (filteredUsers.length ? (
                    <ul className="new-conversation-modal__list">
                        {filteredUsers.map(user => (
                            <li key={user.id}>
                                <button
                                    type="button"
                                    className="new-conversation-modal__user"
                                    disabled={isStarting}
                                    onClick={() => handleSelectUser(user.id)}
                                >
                                    <Avatar initials={user.firstname.charAt(0)} name={user.fullName} />
                                    <span>{user.fullName}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="new-conversation-modal__empty">
                        {trans('pages.messages.noUsersFound')}
                    </p>
                ))}
            </ModalContent>
        </PacoModal>
    )
}
